import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { useState, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { djs } from '@duke-hq/djs';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  HStack,
  Heading,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { EnrolmentForm } from './UpdateEnrolmentFormFields';
import { UpdateEnrolmentReassignFields } from './UpdateEnrolmentReassignFields';
import { scheduleService } from '@duke-hq/svc-api/student/services/schedulerService';
import { overflowScrollbarStyles } from '@duke-hq/ui/styles/overflowScrollbarStyles';

interface UpdateEnrolmentSettingsForm {
  id: number;
  start_at_date: string;
  enrolment_contract_type: string;
  contract_category: string;
  work_schedule: string;
  course_segment: string;
  semester_2_start_at_date: string;
  externalId?: string | null;
  semesterTwoExternalId?: string | null;
  isComplimentary: boolean;
  sem1IsFree: boolean;
  sem2IsFree: boolean;
  sem1FreeReason?: string | null;
  sem2FreeReason?: string | null;
}

type UpdateEnrolmentFormProps = {
  enrolment: NonNullable<RouterOutputs['enrolment']['byIdWithResourceStatus']>;
  onClose: () => void;
};

export function UpdateEnrolmentForm({ enrolment, onClose }: UpdateEnrolmentFormProps) {
  const updateCourseEnrolment = trpcAdmin.enrolment.updateCourseEnrolment.useMutation();
  const utils = trpcAdmin.useUtils();
  const toast = useToast();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const reassignConsultantSubmitRef = useRef<(() => void) | null>(null);
  const [isReassigning, setIsReassigning] = useState(false);

  const defaultValues = {
    id: enrolment.id,
    start_at_date: djs(enrolment.start_at_date).format('YYYY-MM-DD'),
    enrolment_contract_type: enrolment.enrolment_contract_type,
    contract_category: enrolment.contract_category,
    course_segment: enrolment.course_segment,
    work_schedule: enrolment.work_schedule,
    semester_2_start_at_date: enrolment.semester_2_start_at_date
      ? djs(enrolment.semester_2_start_at_date).format('YYYY-MM-DD')
      : djs(scheduleService.getSemesterStartDate(2, enrolment)).format('YYYY-MM-DD'),
    externalId: enrolment?.externalId ?? undefined,
    semesterTwoExternalId: enrolment?.semesterTwoExternalId,
    isComplimentary: enrolment.isComplimentary,
    sem1IsFree: enrolment.sem1IsFree ?? false,
    sem2IsFree: enrolment.sem2IsFree ?? false,
    sem1FreeReason: enrolment.sem1FreeReason ?? undefined,
    sem2FreeReason: enrolment.sem2FreeReason ?? undefined,
  } as const;

  const methods = useForm<UpdateEnrolmentSettingsForm>({
    mode: 'onBlur',
    defaultValues,
  });

  const fieldsThatAlterValues = ['start_at_date', 'work_schedule'] as const;
  const valuesThatAlterSchedule = methods.watch(fieldsThatAlterValues);
  const hasAlteredValueThatImpactsSchedule = fieldsThatAlterValues.some((value, index) => {
    return defaultValues[value] !== valuesThatAlterSchedule[index];
  });

  const [isConfirmingUpdate, setIsConfirmingUpdate] = useState(false);

  const onSubmit = methods.handleSubmit(() => {
    setIsConfirmingUpdate(true);
  });

  const handleConfirmUpdate = async () => {
    try {
      await methods.handleSubmit(
        async (data) => {
          await updateCourseEnrolment.mutateAsync({
            enrolment_id: enrolment.id,
            start_at_date: data.start_at_date,
            user_id: enrolment.user.id,
            enrolment_contract_type: data.enrolment_contract_type,
            contract_category: data.contract_category,
            course_segment: data.course_segment,
            work_schedule: data.work_schedule,
            externalId: data.externalId ?? '',
            semester_2_start_at_date: data.semester_2_start_at_date,
            semesterTwoExternalId: data.semesterTwoExternalId ?? undefined,
            isComplimentary: data.isComplimentary,
            sem1IsFree: data.sem1IsFree,
            sem2IsFree: data.sem2IsFree,
            sem1FreeReason: data.sem1FreeReason ?? undefined,
            sem2FreeReason: data.sem2FreeReason ?? undefined,
          });

          setIsConfirmingUpdate(false);
          onClose();
          void utils.enrolment.byIdWithResourceStatus.refetch({
            enrolmentId: Number(enrolment.id),
          });

          toast({
            status: 'success',
            title: t`Enrolment has been updated`,
          });
        },
        () => {
          setIsConfirmingUpdate(false);
        },
      )();
    } catch (error) {
      toast({
        status: 'error',
        title:
          error instanceof Error
            ? error.message
            : t`An error occurred while updating the enrolment`,
      });
    }
  };

  return (
    <>
      <form onSubmit={onSubmit} style={{ width: '100%', height: '100%' }} noValidate>
        <FormMainContainer>
          <FormContentContainer>
            <Heading fontSize="lg" size="xs" w="full">
              <Trans>Update Enrolment Settings</Trans>
            </Heading>

            <Tabs w="full" index={activeTabIndex} onChange={setActiveTabIndex}>
              <TabList>
                <Tab>
                  <Trans>Enrolment Details</Trans>
                </Tab>
                <Tab>
                  <Trans>Reassign Consultant</Trans>
                </Tab>
              </TabList>

              <TabPanels maxH="450px" overflowY="auto" pr={2} css={overflowScrollbarStyles}>
                <TabPanel>
                  <FormProvider {...methods}>
                    <EnrolmentForm enrolment={enrolment} />
                  </FormProvider>
                </TabPanel>
                <TabPanel>
                  <UpdateEnrolmentReassignFields
                    enrolmentId={enrolment.id}
                    currentValues={{
                      tenant_id: enrolment.tenant_id,
                      consultant_id: enrolment.consultant_id,
                      site_location_id: enrolment.site_location_id,
                    }}
                    onClose={onClose}
                    onSubmitExternal={(submitFn) => {
                      reassignConsultantSubmitRef.current = submitFn;
                    }}
                    onLoadingChange={setIsReassigning}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>

            {Boolean(hasAlteredValueThatImpactsSchedule) && (
              <Alert status="warning" mt={4}>
                <HStack>
                  <AlertIcon />
                </HStack>
                <AlertDescription>
                  <Text fontSize="sm">
                    <Trans>This will rebuild the student enrolment schedule. This is final.</Trans>
                  </Text>
                </AlertDescription>
              </Alert>
            )}
          </FormContentContainer>

          <FormFooterContainer>
            {activeTabIndex === 0 ? (
              <Button isLoading={updateCourseEnrolment.isPending} type="submit">
                {hasAlteredValueThatImpactsSchedule ? t`Update and rebuild schedule` : t`Update`}
              </Button>
            ) : (
              <Button
                isLoading={isReassigning}
                onClick={() => {
                  reassignConsultantSubmitRef.current?.();
                }}
              >
                <Trans>Update</Trans>
              </Button>
            )}
          </FormFooterContainer>
        </FormMainContainer>
      </form>

      <ConfirmationDialog
        isOpen={isConfirmingUpdate}
        onClose={() => {
          setIsConfirmingUpdate(false);
        }}
        title={<Trans>Update enrolment</Trans>}
        body={
          <Trans>
            Are you sure you want to update this enrolment and rebuild its schedule? This is final.
          </Trans>
        }
        onConfirm={() => {
          void handleConfirmUpdate();
        }}
        confirmLabel={<Trans>Yes, rebuild schedule</Trans>}
        variant="destructive"
        isLoading={updateCourseEnrolment.isPending}
      />
    </>
  );
}
